/* @flow */
//
// NOTICE: can't conflict with src/createSsrRoutes
//
import * as React from 'react';
import { IndexRoute, Route, Redirect } from 'react-router';

import AddedDeviceConfirmationRoute from 'app/components/AddedDeviceConfirmationRoute';
import AdminSignupRedirect from 'admin/components/AdminSignupRedirect';
import AdminUpgradePlanView from 'admin/components/AdminUpgradePlan/AdminUpgradePlanView';
import AdminUpgradePlanScheduledMaintenancePage from 'admin/components/AdminUpgradePlan/AdminUpgradePlanScheduledMaintenancePage';
import App from 'app/components/App';
import AppLayoutContainer from 'app/containers/AppLayoutContainer';
import AppSidebarLayout from 'app/components/AppSidebarLayout';
import AppUpsellPageContainer from 'user/components/AppUpsellPageContainer';
import BrokenShareButtonPage from 'app/components/BrokenShareButtonPage';
import { CoachingRoutes, CoachingIsolatedRoutes } from 'coaching/routes';
import ConfirmEmailPage from 'auth/components/ConfirmEmailPage';
import CookiesDisabledPage from 'app/components/CookiesDisabledPage';
import DebugInfoView from 'misc/components/DebugInfoView';
import DeleteAccountPage from 'user/components/DeleteAccountPage';
import EmailUnsubscribeView from 'user/components/EmailUnsubscribeView';
import ErrorView from 'app/components/ErrorView';
import ForgotPasswordPage from 'auth/components/ForgotPasswordPage';
import FullPageComposerView from 'chat/components/FullPageComposer/FullPageComposerView';
import GetBirthdateForChat from 'chat/components/GetBirthdateForChat';
import GroupsRoutes from 'groups/routes';
import {
  LearnMorePage as IndividualPlansLearnMorePage,
  IOSPurchaseTerms as IndividualPlansIOSPurchaseTerms,
  AndroidPurchaseTerms as IndividualPlansAndroidPurchaseTerms,
} from 'individualPlans/components';
import JoinRoutes from 'join/routes';
import LeaveChatView from 'chat/components/LeaveChatView';
import MarketingEmailUnsubscribeView from 'user/components/MarketingEmailUnsubscribeView';
import MarketingLayout from 'components/MarketingLayout';
import MergeDirectAddeePage from 'auth/components/MergeDirectAddeePage';
import NotFoundPage from 'app/components/NotFoundPage';
import OrganizationsRoutes from 'organizations/routes';
import ParentalConsentPage from 'user/components/ParentalConsentPage';
import QuickLinkView from 'app/components/QuickLinkView';
import LinkedAccountSuccessView from 'app/components/LinkedAccountSuccessView';
import LinkedAccountFailureView from 'app/components/LinkedAccountFailureView';
import ReportChatView from 'chat/components/ReportChatView';
import ResetPasswordPage from 'auth/components/ResetPasswordPage';
import RsvpView from 'rsvp/RsvpView';
import SessionExpiredPage from 'auth/components/SessionExpiredPage';
import SessionSyncPage from 'auth/components/SessionSyncPage';
import SettingsRoutes from 'settings/routes';
import SetupRoutes from 'setup/routes';
import ShareRemindPage from 'user/components/ShareRemindPage';
import SignInPage from 'auth/components/SignInPage';
import SignOutPage from 'auth/components/SignOutPage';
import SupportCallPage from 'support/components/SupportCallPage';
import TextAppLinkPage from 'user/components/TextAppLinkPage';
import TranscriptRequestApproveView from 'user/components/TranscriptRequestApproveView';
import IntegrationsRoutes from 'integrations/routes';
import ACIPage from 'admin/components/ACIPage';
import LTIPage from 'admin/components/LTIPage';
import { goLandingIfAuthed, logOutAndConfirm } from 'lib/ClientRouteLogic';
import RequireCookies from 'lib/RequireCookies';
import OrganizationEngagementViewDataContainer from 'organizations/components/OrganizationEngagementView';
import SchoolViewDataContainer from 'schools/components/SchoolView';
import RequireDesktop from 'lib/RequireDesktop';
import RequireUser from 'lib/RequireUser';
import MergingUserContainer from 'user/containers/MergingUserContainer';

import { RESET_PASSWORD_PATH } from 'constants/AuthConstants';
import { MERGING_USER_PATH } from 'user/UserConstants';

// TODO: Dynamically load routes
export default (
  <Route
    component={App}
    // $FlowFixMe
    onEnter={App.onEnter}
    // $FlowFixMe
    onChange={App.onChange}>
    <Redirect from="/" to="/classes" />
    <Redirect from="/landing" to="/classes" />
    <Redirect from="/admin(/*)" to="/classes" />
    <Redirect from="/classes/all" to="/classes/" />
    <Redirect from="/profile" to="/settings/profile" />
    <Redirect from="/profile/widgets" to="/settings/widgets" />
    <Redirect from="/profile/exports" to="/settings/export" />
    <Redirect from="/sign_in" to="/log_in" />
    <Redirect from="/sign_out" to="/log_out" />
    <Redirect from="/get_our_app" to="/get_the_app(/)" />

    {CoachingIsolatedRoutes}

    {/* run RequireDesktop before redirecting to any /r/ url, since mobile clients don't understand it, and
     * RequireDesktop lets mobile users with the app deeplink to the given url (so adding /r/ breaks it)
     */}
    <Route onEnter={RequireDesktop}>
      <Redirect from="/chat(/)" to="/classes/r/messages" />
      <Redirect from="/inbox(/)" to="/classes/r/messages" />
      <Redirect from="/chat/:chatId" to="/classes/r/messages/:chatId" />
    </Route>

    <Redirect from="/schools_desktop" to="/get_the_app" />
    <Redirect from="/pay(/)" to="/rsvp" />
    <Route path="/rsvp(/)" component={RsvpView} />
    <Route path="/individual_plans/learn_more" component={IndividualPlansLearnMorePage} />
    <Route
      path="/individual_plans/purchase_terms/ios"
      component={IndividualPlansIOSPurchaseTerms}
    />
    <Route
      path="/individual_plans/purchase_terms/android"
      component={IndividualPlansAndroidPurchaseTerms}
    />
    <Route path="/district/:organizationId" component={OrganizationEngagementViewDataContainer} />
    <Route path="/school/:organizationId" component={SchoolViewDataContainer} />
    <Route component={MarketingLayout}>
      <Route path="/f/cookies_disabled" component={CookiesDisabledPage} />
      <Route onEnter={RequireUser}>
        <Route path="/f/support_call" component={SupportCallPage} />
      </Route>
      <Route
        path="/get_the_app"
        component={AppUpsellPageContainer}
        onEnter={AppUpsellPageContainer.onEnter}
      />
      <Route path="/log_out" component={SignOutPage} onEnter={SignOutPage.onEnter} />
      <Route path="/send_the_app" component={TextAppLinkPage} />
      <Route path="/session/expired" component={SessionExpiredPage} />
      <Route path="/share_remind" component={ShareRemindPage} />
      <Route path="/error" component={ErrorView} />
      <Route path="/debug" component={DebugInfoView} />
      <Route path="/not_found" component={NotFoundPage} />
      <Route path="/f/broken_share" component={BrokenShareButtonPage} />
    </Route>
    <Route onEnter={RequireCookies}>
      {/* no layout routes */}
      <Route component={MarketingLayout}>
        {IntegrationsRoutes}
        <Route path="/chat/enroll/:token" component={GetBirthdateForChat} />

        <Route
          path="/chat/report/:token"
          component={ReportChatView}
          onEnter={ReportChatView.onEnter}
        />

        <Route
          path="/chat/leave/:token"
          component={LeaveChatView}
          onEnter={LeaveChatView.onEnter}
        />
      </Route>

      <Route path="install_aci/:organizationUUID/:providerKey" component={ACIPage} />
      <Route path="install_lti/:organizationUUID/:providerKey" component={LTIPage} />

      <Route path="admin_upgrade(/)">
        <Redirect from="contact_us" to="/admin_upgrade" />
        <Route path="maintenance" component={AdminUpgradePlanScheduledMaintenancePage} />
        <Route
          path=":orgUuid"
          component={({ router }) => <AdminUpgradePlanView orgUuid={router.params.orgUuid} />}
        />
        <IndexRoute component={AdminUpgradePlanView} />
      </Route>
      <Route component={AppLayoutContainer}>
        {SettingsRoutes}

        <Route component={AppSidebarLayout}>
          {GroupsRoutes}
          {OrganizationsRoutes}
          {CoachingRoutes}
          <Route
            path="/compose(/)"
            component={FullPageComposerView}
            onEnter={FullPageComposerView.onEnter}
          />
        </Route>
      </Route>
      <Route path="/quicklink/:token" component={QuickLinkView} onEnter={QuickLinkView.onEnter} />

      {/*
         auth/success is first routed to a page that tries to close the window if it's a popup,
         so this route has to have a different uri
      */}
      <Route path="auth_success" component={LinkedAccountSuccessView} />
      <Route path="auth/failure" component={LinkedAccountFailureView} />
      {SetupRoutes}
      {JoinRoutes}
      <Route component={MarketingLayout}>
        <Route
          path="/school/sign_up"
          component={AdminSignupRedirect}
          onEnter={AdminSignupRedirect.onEnter}
        />

        <Route path="/confirm_added_devices" components={AddedDeviceConfirmationRoute} />

        <Route path="/log_in" component={SignInPage} onEnter={goLandingIfAuthed} />
        <Route path="/confirm_email/:token" component={ConfirmEmailPage} />
        <Route path="/direct_addee/:token" component={MergeDirectAddeePage} />
        <Route
          path="/log_out_and_confirm/:token"
          component={SignInPage}
          onEnter={logOutAndConfirm}
        />
        {/*
      Users can reset password by SMS or email. SMS sends a link to
      authenticate the user that then redirects here. Email contains a link to
      /reset_password/:token that we handle in r101-frontend Node, setting a
      cookie, and then redirect here.
      */}
        <Route path={RESET_PASSWORD_PATH} component={ResetPasswordPage} />
        <Route path="/forgot_password" component={ForgotPasswordPage} />
        <Route path="/user_changed" component={SessionSyncPage} />
        {/* FIXME stop leaking token to delete user account */}
        <Route path="/delete_account/:token" component={DeleteAccountPage} />
        <Route
          path="/email/unsubscribe/:token"
          component={EmailUnsubscribeView}
          onEnter={EmailUnsubscribeView.onEnter}
        />
        <Route path="/unsubscribe/:token" component={MarketingEmailUnsubscribeView} />

        <Route path="/transcript/:token" component={TranscriptRequestApproveView} />

        <Route
          path="/parental_consent"
          component={ParentalConsentPage}
          onEnter={ParentalConsentPage.onEnter}
        />
      </Route>

      <Route path={MERGING_USER_PATH} component={MergingUserContainer} />

      <Route path="*" component={NotFoundPage} />
    </Route>
  </Route>
);
